import { useEffect, useState } from 'react';
import { adminApi } from './api';
import ConfirmDialog from './ConfirmDialog';
import './PremiumAccessAudit.css';

const SCOPES = [
  ['all', 'Tous les accès suivis'],
  ['payment', 'Justifiés par un paiement'],
  ['admin', 'Attribués manuellement'],
  ['referral', 'Parrainages'],
  ['review', 'À examiner'],
  ['paid_no_access', 'Paiement sans accès'],
  ['blocked', 'Bloqués'],
  ['history', 'Historiques expirés ou sans droits'],
];
const LABELS = {
  payment: 'Paiement confirmé',
  admin: 'Attribution administrative',
  referral: 'Parrainage',
  review: 'À examiner',
  paid_no_access: 'Paiement sans accès',
  blocked: 'Accès bloqué',
  history: 'Aucun accès actuel',
};
function date(value) {
  if (!value) return '—';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString('fr-FR', {
    dateStyle: 'short', timeStyle: 'short',
  });
}
function displayName(candidate) {
  return candidate.pseudonym
    || [candidate.firstName, candidate.lastName].filter(Boolean).join(' ')
    || `Candidat #${candidate.id}`;
}

export default function PremiumAccessAudit({ onUnauthorized }) {
  const [filters, setFilters] = useState({ status: 'all', search: '', page: 1 });
  const [searchDraft, setSearchDraft] = useState('');
  const [reloadKey, setReloadKey] = useState(0);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [decision, setDecision] = useState(null);
  const [reason, setReason] = useState('');
  const [eventsUser, setEventsUser] = useState(null);
  const [events, setEvents] = useState(null);
  const [eventError, setEventError] = useState('');

  useEffect(() => {
    let live = true;
    setLoading(true);
    setError('');
    adminApi.premiumAccessAudit(filters)
      .then(result => { if (live) setData(result); })
      .catch(failure => {
        if (!live) return;
        setError(failure.message || 'Impossible de contrôler les accès Premium');
        if (failure.status === 401) onUnauthorized?.();
      })
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, reloadKey]);

  function setFilter(field, value) {
    setFilters(current => ({ ...current, [field]: value, page: 1 }));
  }
  function beginDecision(candidate, operation) {
    setReason('');
    setError('');
    setDecision({ candidate, operation });
  }

  async function submitDecision() {
    if (!decision || busy) return;
    const text = reason.trim();
    if (text.length < 10 || text.length > 1000) {
      setError('Indiquez un motif explicite de 10 à 1000 caractères.');
      return;
    }
    setBusy(true);
    setError('');
    const id = decision.candidate.id;
    try {
      if (decision.operation === 'block') {
        const result = await adminApi.blockCandidatePremium(id, text);
        setNotice(`Blocage enregistré pour ${displayName(decision.candidate)} : ${result.entitlementsRevoked ?? result.rightsRevoked} droit(s) révoqué(s), ${result.licensesRevoked} licence(s) hors connexion signalée(s) révoquée(s).`);
      } else {
        await adminApi.unblockCandidatePremium(id, text);
        setNotice(`Blocage levé pour ${displayName(decision.candidate)}. Les anciens droits ne sont pas réactivés.`);
      }
      setDecision(null);
      setReloadKey(n => n + 1);
      if (eventsUser?.id === id) {
        const history = await adminApi.premiumAccessAuditEvents(id);
        setEvents(history.events || []);
      }
    } catch (failure) {
      setError(failure.message || 'Opération non enregistrée');
      if (failure.status === 401) onUnauthorized?.();
    } finally {
      setBusy(false);
    }
  }

  async function toggleEvents(candidate) {
    if (eventsUser?.id === candidate.id) {
      setEventsUser(null);
      setEvents(null);
      return;
    }
    setEventsUser(candidate);
    setEvents(null);
    setEventError('');
    try {
      const response = await adminApi.premiumAccessAuditEvents(candidate.id);
      setEvents(response.events || []);
    } catch (failure) {
      setEventError(failure.message || 'Journal indisponible');
      if (failure.status === 401) onUnauthorized?.();
    }
  }

  const summary = data?.summary;
  const pagination = data?.pagination;
  const candidates = data?.candidates || [];

  return (
    <section className="bo-panel bo-premium-audit" aria-label="Audit des accès Premium">
      <div className="bo-panel__header">
        <div>
          <p className="bo-eyebrow">Sécurité et cohérence</p>
          <h3>Audit des accès Premium</h3>
          <p className="bo-muted">
            Rapprochement candidat par candidat : droits actifs, paiements fictifs confirmés,
            attributions administratives et parrainages. Une anomalie est à examiner, pas une preuve de fraude.
          </p>
        </div>
        <button type="button" className="bo-button bo-button--ghost"
          onClick={() => setReloadKey(n => n + 1)} disabled={loading || busy}>
          Actualiser l’audit
        </button>
      </div>

      {summary && (
        <div className="bo-premium-audit__metrics">
          <div><span>Accès à du contenu Premium</span><strong>{summary.premiumActive}</strong></div>
          <div><span>Premium complet</span><strong>{summary.fullPremium}</strong></div>
          <div><span>Parrainage partiel uniquement</span><strong>{summary.selectedOnly}</strong></div>
          <div><span>Candidats avec paiement non expiré</span><strong>{summary.candidatesWithValidPayment}</strong></div>
          <div><span>Justifiés par paiement fictif</span><strong>{summary.payment}</strong></div>
          <div><span>Attributions administratives</span><strong>{summary.admin}</strong></div>
          <div><span>Parrainages</span><strong>{summary.referral}</strong></div>
          <div><span>À examiner</span><strong>{summary.review + summary.paidNoAccess}</strong></div>
          <div><span>Blocages actifs</span><strong>{summary.blocked}</strong></div>
          <div><span>Historiques sans droit actif</span><strong>{summary.history}</strong></div>
        </div>
      )}

      <form className="bo-premium-audit__filters" aria-label="Filtres des accès Premium"
        onSubmit={event => { event.preventDefault(); setFilter('search', searchDraft.trim()); }}>
        <label>Rechercher
          <input className="bo-input" type="search" maxLength={100} value={searchDraft}
            placeholder="Pseudo, email ou identifiant"
            onChange={event => setSearchDraft(event.target.value)} />
        </label>
        <label>Situation
          <select className="bo-input" value={filters.status}
            onChange={event => setFilter('status', event.target.value)}>
            {SCOPES.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
          </select>
        </label>
        <button className="bo-button bo-button--primary" type="submit">Rechercher</button>
      </form>

      {notice && <p className="bo-alert bo-alert--success" role="status">{notice}</p>}
      {error && <p className="bo-alert bo-alert--error" role="alert">{error}</p>}
      {loading && <p className="bo-muted" role="status">Analyse des droits en cours…</p>}

      {!loading && !error && (
        <>
          {!candidates.length ? (
            <p className="bo-muted">Aucun candidat pour ce filtre.</p>
          ) : (
            <div className="bo-table-wrap bo-premium-audit__table-wrap">
              <table className="bo-table">
                <thead><tr>
                  <th>Candidat</th><th>Statut</th><th>Accès Premium</th>
                  <th>Paiements encore valides</th><th>Justificatifs</th><th>Contrôle</th>
                </tr></thead>
                <tbody>{candidates.map(candidate => (
                  <tr key={candidate.id}>
                    <td><strong>{displayName(candidate)}</strong>
                      <small className="bo-premium-audit__secondary">
                        #{candidate.id} · {candidate.email}
                      </small></td>
                    <td><span className={`bo-premium-audit__status is-${candidate.status}`}>
                      {LABELS[candidate.status] || candidate.status}
                    </span></td>
                    <td>{candidate.activePremium
                      ? candidate.fullRights > 0 ? 'Complet' : 'Sélection de contenus'
                      : 'Aucun accès actif'}</td>
                    <td>{candidate.validPayments}</td>
                    <td>
                      <small>Payés : {candidate.paymentRights} · Admin : {candidate.adminRights}</small>
                      <small className="bo-premium-audit__secondary">
                        Parrainages : {candidate.referralRights} · Inconnus : {candidate.unknownRights}
                      </small>
                    </td>
                    <td>
                      <div className="bo-actions">
                        <button type="button" className="bo-button bo-button--ghost"
                          onClick={() => toggleEvents(candidate)}>
                          {eventsUser?.id === candidate.id ? 'Fermer le journal' : 'Journal'}
                        </button>
                        {!candidate.blocked ? (
                          <button type="button" className="bo-button bo-button--danger-ghost"
                            disabled={busy} onClick={() => beginDecision(candidate, 'block')}>
                            Signaler et bloquer
                          </button>
                        ) : data?.canUnblock ? (
                          <button type="button" className="bo-button bo-button--ghost"
                            disabled={busy} onClick={() => beginDecision(candidate, 'unblock')}>
                            Lever le blocage
                          </button>
                        ) : <span className="bo-premium-audit__secondary">Blocage actif</span>}
                      </div>
                    </td>
                  </tr>
                ))}</tbody>
              </table>
            </div>
          )}

          {eventsUser && (
            <section className="bo-premium-audit__journal" aria-label="Journal de contrôle Premium">
              <h4>Journal — {displayName(eventsUser)}</h4>
              {eventError && <p role="alert">{eventError}</p>}
              {events === null && !eventError && <p>Lecture du journal…</p>}
              {events?.length === 0 && <p>Aucun blocage ou déblocage enregistré.</p>}
              {events?.map(event => (
                <div key={event.id}>
                  <strong>{event.action === 'blocked' ? 'Blocage' : 'Levée du blocage'}</strong>
                  <span>{date(event.createdAt)} · {event.admin}</span>
                  <p>{event.reason}</p>
                </div>
              ))}
            </section>
          )}
          <div className="bo-premium-audit__pagination">
            <small>{pagination?.total || 0} candidat(s) · Page {pagination?.page || 1} sur {Math.max(1, pagination?.totalPages || 0)}</small>
            <div className="bo-actions">
              <button type="button" className="bo-button bo-button--ghost"
                disabled={loading || filters.page <= 1}
                onClick={() => setFilters(f => ({ ...f, page: f.page - 1 }))}>Précédent</button>
              <button type="button" className="bo-button bo-button--ghost"
                disabled={loading || filters.page >= (pagination?.totalPages || 1)}
                onClick={() => setFilters(f => ({ ...f, page: f.page + 1 }))}>Suivant</button>
            </div>
          </div>
          <p className="bo-muted bo-premium-audit__caution">
            Les chiffres comptent des candidats distincts, et non le nombre total de transactions.
            Un candidat peut cumuler plusieurs sources légitimes ; les catégories
            « paiement », « administration » et « parrainage » donnent une source
            principale et les justificatifs détaillés restent visibles dans le tableau.
            Les montants et paiements sont fictifs. Les signalements restent traçables,
            mais ne constituent pas une preuve de fraude. La révocation d’une licence
            hors connexion est appliquée au serveur et à la prochaine synchronisation :
            sans Internet, un appareil déjà autorisé peut conserver l’accès
            jusqu’à l’expiration de sa licence locale.
          </p>
        </>
      )}

      <ConfirmDialog open={Boolean(decision)}
        title={decision?.operation === 'block' ? 'Signaler et bloquer Premium' : 'Lever le blocage Premium'}
        description={decision?.operation === 'block'
          ? 'Les droits Premium et licences enregistrées seront révoqués, y compris les droits expirés. Le dossier et les preuves de paiement resteront conservés.'
          : 'Seul le super-administrateur peut lever le blocage. Les anciens droits et licences ne seront pas réactivés.'}
        confirmLabel={decision?.operation === 'block' ? 'Confirmer le blocage' : 'Confirmer la levée'}
        busy={busy}
        details={decision && <div className="bo-premium-audit__reason">
          <strong>{displayName(decision.candidate)} · #{decision.candidate.id}</strong>
          <label htmlFor="premium-block-reason">Motif obligatoire (10 caractères minimum)
            <textarea id="premium-block-reason" className="bo-input" rows={3}
              maxLength={1000} value={reason}
              onChange={event => setReason(event.target.value)}
              placeholder="Indiquez précisément le motif et les éléments à vérifier" />
          </label>
          {error && <p role="alert">{error}</p>}
        </div>}
        onCancel={() => { if (!busy) { setDecision(null); setError(''); } }}
        onConfirm={submitDecision} />
    </section>
  );
}
